import { createFileRoute } from '@tanstack/react-router';
import type { SortingState } from '@tanstack/react-table';

import { EquitiesExploreTable } from '#/components/index/EquitiesExploreTable';
import { ExploreConceptsSection } from '#/components/index/ExploreConceptsSection';
import { FixedIncomeDataSection } from '#/components/index/FixedIncomeDataSection';
import { HomepageBelowFoldSection } from '#/components/index/HomepageBelowFoldSection';
import { HomepageSkeleton } from '#/components/index/HomepageSkeleton';
import { MarketIndexCardsSection } from '#/components/index/MarketIndexCardsSection';
import { MoreAboutFinraSection } from '#/components/index/MoreAboutFinraSection';
import { MutualFundsExploreTable } from '#/components/index/MutualFundsExploreTable';
import { NewsSection } from '#/components/index/NewsSection';
import { DevSpacer } from '#/components/layout/DevSpacer';
import { HomepageLayout } from '#/components/layout/HomepageLayout';
import { HomepageNavigation } from '#/components/layout/HomepageNavigation';
import { ErrorBoundary } from '#/components/ui/ErrorBoundary';
import { useHomepageContent } from '#/content/homepage';
import { homepageContentOptions } from '#/content/queries';
import {
  INTERIOR_NAV_CTA,
  INTERIOR_NAV_PRODUCT_TYPES,
  INTERIOR_NAV_SECTORS,
} from '#/lib/category-navigation';
import { queryClient } from '#/lib/query-client';

const HOMEPAGE_MUTUAL_FUND_SORTING: SortingState = [
  { id: 'expense_ratio', desc: false },
];

const HOMEPAGE_EQUITY_SORTING: SortingState = [
  { id: 'trailing_5yr_return', desc: true },
];

export const Route = createFileRoute('/')({
  staticData: { breadcrumb: 'Home' },
  pendingComponent: HomepageSkeleton,
  // Warm the homepage content cache before render and ensure content is available.
  loader: () =>
    queryClient.ensureQueryData(homepageContentOptions()).catch(() => null),
  component: HomePage,
});

function HomePage() {
  const { content } = useHomepageContent();
  return (
    <>
      <DevSpacer className="col-full" />

      <HomepageNavigation
        productTypes={INTERIOR_NAV_PRODUCT_TYPES}
        sectors={INTERIOR_NAV_SECTORS}
        cta={INTERIOR_NAV_CTA}
        hero={content}
      />

      <HomepageLayout className="flow flow-inner-60 mt-80">
        {content.exploreConcepts ? (
          <section
            className="col-feature"
            aria-label={content.exploreConcepts.title}
          >
            <ExploreConceptsSection content={content.exploreConcepts} />
          </section>
        ) : null}

        <div className="col-full grid-cols-layout from-slate-10 to-gray-10 flow flow-inner-60 grid min-w-0 bg-linear-to-b bg-size-[100%_66dvh] bg-no-repeat pt-60">
          {content.news ? (
            <section
              className="col-feature"
              aria-label={content.news.title ?? 'In the News'}
            >
              <ErrorBoundary>
                <NewsSection content={content.news} />
              </ErrorBoundary>
            </section>
          ) : null}

          <section className="col-feature">
            <ErrorBoundary>
              <FixedIncomeDataSection />
            </ErrorBoundary>
          </section>

          <HomepageBelowFoldSection className="col-full grid-cols-layout flow flow-inner-60 grid min-w-0">
            <section className="col-feature">
              <ErrorBoundary>
                <MarketIndexCardsSection />
              </ErrorBoundary>
            </section>

            <section className="col-feature flow-16 grid min-w-0 sm:gap-20 lg:grid-cols-2">
              <ErrorBoundary>
                <MutualFundsExploreTable
                  id="homepage-mutual-funds-by-cost"
                  title="Mutual Funds by Cost"
                  visibleColumns={[
                    'ticker',
                    'expense_ratio',
                    'price',
                    'return_5Y_annualized',
                  ]}
                  initialSorting={HOMEPAGE_MUTUAL_FUND_SORTING}
                  limit={5}
                  compact
                />
              </ErrorBoundary>

              <ErrorBoundary>
                <EquitiesExploreTable
                  id="homepage-equities-by-returns"
                  title="Equities by 5-Year Trailing Returns"
                  visibleColumns={[
                    'ticker',
                    'price',
                    'trailing_5yr_return',
                    'market_cap',
                  ]}
                  initialSorting={HOMEPAGE_EQUITY_SORTING}
                  limit={5}
                  compact
                />
              </ErrorBoundary>
            </section>
          </HomepageBelowFoldSection>

          {content.aboutFinra ? (
            <section
              className="col-feature flow-20"
              aria-label="More About FINRA"
            >
              <MoreAboutFinraSection content={content.aboutFinra} />
            </section>
          ) : null}
        </div>
      </HomepageLayout>
    </>
  );
}
